/**
 * A SEQUÊNCIA DE ESCRITAS DE UM PUBLICAR, COMO DADO — para poder ser testada.
 *
 * ── O DEFEITO QUE ESTE MÓDULO EXISTE PARA IMPEDIR (`T4.13`) ───────────────────────────────────
 *
 * Ele, na letra: *"Eu tento publicar isso aqui / Vou colocar como pronta / Não deu publicar agora
 * / (...) Na real, ela disse que conseguiu, mas não conseguiu de verdade / Tentei acessar e não
 * foi."*
 *
 * Publicar é **uma escrita seguida de N escritas**, e não havia volta atrás. A ficha ia primeiro —
 * a ordem é obrigatória, porque a regra que autoriza escrever uma página consulta a ficha
 * (`fichaDe(docId)` em `firestore.rules`) e sem ela nega. Se uma página falhasse no meio, a ficha
 * **já estava** na biblioteca com `estado: "publicado"`:
 *
 *   · o quadrinho aparecia como publicado em "Meus quadrinhos" — o estado é lido de
 *     `biblioteca/{id}`, e a ficha estava lá;
 *   · a tela mostrava "Não deu para publicar agora";
 *   · e abrir o item na biblioteca não achava as páginas.
 *
 * A ficha é o **anúncio**; as páginas são a **história**. O anúncio subia sozinho.
 *
 * ── POR QUE DUAS FASES E NÃO UMA TRANSAÇÃO ───────────────────────────────────────────────────
 *
 * `writeBatch` não resolve: as regras do Firestore avaliam cada escrita do lote contra o estado
 * **anterior** ao lote, então a página continuaria sendo negada por falta de ficha.
 *
 * O que resolve é a ficha **nascer invisível**. A regra de leitura da biblioteca é
 * `estado == 'publicado'`; a de escrita de página exige apenas autoria e não-finalizado. Logo uma
 * ficha em `rascunho` autoriza as páginas e não aparece para ninguém. Só depois que todas as
 * páginas subiram é que a ficha vira o que a pessoa escolheu.
 *
 * Falhou no meio? Fica uma ficha `rascunho` sem anúncio: a biblioteca não a mostra, "Meus
 * quadrinhos" a lê como guardada, e a mensagem de erro passa a ser verdadeira.
 *
 * ── POR QUE ISTO É UM MÓDULO, E NÃO CINCO LINHAS DENTRO DO COMPONENTE ────────────────────────
 *
 * Porque a propriedade que importa é uma propriedade da **ordem**, e ordem dentro de um `async`
 * com Firebase só se verifica com o Firebase de pé. Como dado, ela se verifica com `expect`.
 */
import { Publicacao } from "../api/documento.types";

/** Uma escrita, e só o que decide se a ordem está certa. */
export type EscritaDePublicacao =
  | { tipo: "ficha"; anuncia: boolean; publicacao: Publicacao }
  | { tipo: "pagina"; numero: number };

/**
 * A ordem das escritas de um publicar.
 *
 * @param quantasPaginas  quantas páginas o quadrinho tem
 * @param publicacao      o que a pessoa escolheu — o estado FINAL, o do anúncio
 * @param jaEstaNaBiblioteca
 *   A ficha já existe? Então a fase 1 não tem o que fazer, e **não deve** fazer: reescrevê-la como
 *   `rascunho`/não-finalizado seria mudar o estado por conta própria, e num item já finalizado a
 *   regra recusaria — o que quebraria o despublicar que o `T2.61` garante ("vai dessa forma, seja
 *   publicado ou não"). Com a ficha já lá, as páginas já estão autorizadas.
 */
export function planoDePublicacao(
  quantasPaginas: number,
  publicacao: Publicacao,
  jaEstaNaBiblioteca: boolean
): EscritaDePublicacao[] {
  const passos: EscritaDePublicacao[] = [];

  if (!jaEstaNaBiblioteca) {
    // FASE 1 — a ficha invisível. `rascunho` para a biblioteca não mostrar, e `finalizado: false`
    // porque a regra de escrita de página exige exatamente isso.
    passos.push({
      tipo: "ficha",
      anuncia: false,
      publicacao: { ...publicacao, estado: "rascunho", finalizado: false },
    });
  }

  // FASE 2 — a história.
  for (let i = 1; i <= quantasPaginas; i++) passos.push({ tipo: "pagina", numero: i });

  // FASE 3 — e só agora o anúncio.
  passos.push({ tipo: "ficha", anuncia: true, publicacao });

  return passos;
}

/**
 * A invariante, dita em uma linha para poder ser cobrada.
 *
 * **Nenhuma página pode ser escrita depois do anúncio, e o anúncio tem que ser o último passo.**
 * É o que garante que uma falha em qualquer ponto deixe o quadrinho não anunciado em vez de
 * anunciado pela metade.
 */
export function anuncioEhOUltimoPasso(passos: EscritaDePublicacao[]): boolean {
  const iAnuncio = passos.findIndex((p) => p.tipo === "ficha" && p.anuncia);
  if (iAnuncio === -1) return false;
  return iAnuncio === passos.length - 1;
}
